import {
  AdditionalBusinessCompletedProperties,
  AnalyticsEvents,
  BusinessCreateFailedProperties,
  BusinessCreateRequestedProperties,
  BusinessCreateSucceededProperties,
  OnboardingBrandCardSubmittedProperties,
  OnboardingCompletedProperties,
  OnboardingPhaseViewedProperties,
  OnboardingPresenceSubmittedProperties,
  OnboardingScrapeCompletedProperties,
  OnboardingStartedProperties,
  OnboardingStepWebsiteUrlSkippedProperties,
  OnboardingStepWebsiteUrlSubmittedProperties,
  OnboardingVisualsSubmittedProperties,
} from '@mondaygpt/analytics-events'
import {
  GENERAL_GOAL_OPTIONS,
  OnboardingFlow,
  PENDING_BUSINESS_ID,
  FEATURE_FLAGS,
} from '@mondaygpt/constants'
import { parseHostname } from '@mondaygpt/types'
import { AnimatePresence, motion, useIsPresent } from 'framer-motion'
import { useAnalytics, useCliAuthCallback, useToast } from 'hooks'
import {
  selectBusinessSetupAction,
  selectRequiresBusinessCreation,
  updateCurrentUser,
} from 'modules/authentication'
import {
  createBusinessAction,
  markBusinessSetupCompleteAction,
  updateBusinessAction,
} from 'modules/business'
import { selectBusinessId } from 'modules/common/selectors/authentication-view.selector'
import { selectCurrentUser } from 'modules/common'
import { useFeatureGate } from 'modules/feature-flags'
import { useAppDispatch, useAppSelector } from 'modules/core'
import {
  clearBrandLearningOverride,
  seedBrandLearningFromPreview,
  setBrandLearningOverride,
} from 'modules/brand-learning/api/brand-learning-api'
import { downloadLogo, patchBrandKit } from 'modules/creative/api/brand-kit-api'
import { useBrandKitStore } from 'modules/creative/store/brand-kit-store'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { selectOnboardingFlow, selectOnboardingUserStateInitialized } from '../slices'
import { BrandCardData, BrandCardStep } from './BrandCardStep'
import { ConversationStep } from './ConversationStep'
import { classifyContentFocus } from './content-focus'
import { DarkPaywallStep, type TrialPersonalization } from './DarkPaywallStep'
import { SegwayStep } from './SegwayStep'
import { FocusStep } from './FocusStep'
import { GoalsStep } from './GoalsStep'
import { getFlowChrome } from './flow-chrome'
import { normalizeGoals, normalizeTones } from './scan-normalizers'
import { OnboardingShell } from './OnboardingShell'
import { ScanningStep } from './ScanningStep'
import {
  parseWebsiteHost,
  pickScrapeKind,
  ScanStatus,
  ScrapeInput,
  useScanStream,
} from './use-scan-stream'
import {
  getBatchPendingPreviews,
  getBatchSummary,
  useBrandLearningPreviewBatch,
} from './use-brand-learning-preview-batch'
import {
  EMPTY_PRESENCE,
  PRESENCE_FIELD_LABELS,
  PresenceData,
  type PresenceSubmitMeta,
  PresenceStep,
} from './PresenceStep'

type Phase =
  | 'presence'
  | 'scanning'
  | 'confirm'
  | 'creatingBusiness'
  | 'conversation'
  | 'goals'
  | 'focus'
  | 'paywall'
  | 'done'

/** Depth order for the browser-history wiring: push an entry only on forward moves. */
const PHASE_DEPTH: Record<Phase, number> = {
  presence: 0,
  scanning: 1,
  confirm: 2,
  creatingBusiness: 3,
  conversation: 4,
  goals: 5,
  focus: 6,
  paywall: 7,
  done: 8,
}

/**
 * The back chain, shared by the reducer's 'back' case and the browser popstate
 * handler. The conversational flow mirrors the classic chain: paywall → goals →
 * conversation(chat) → confirm → presence. scanning/creatingBusiness are
 * no-escape (their async work is in-flight); the chat's thread is persisted
 * server-side (kind='onboarding') and re-hydrated on return.
 */
function prevPhaseFor(phase: Phase, conversation: boolean | undefined): Phase | undefined {
  const prev: Record<Phase, Phase | undefined> = {
    presence: undefined,
    scanning: undefined,
    confirm: 'presence',
    creatingBusiness: undefined,
    conversation: 'confirm',
    goals: conversation ? 'conversation' : 'confirm',
    focus: 'goals',
    paywall: conversation ? 'goals' : 'focus',
    done: undefined,
  }
  return prev[phase]
}

// Min gap between two forward phase advances. The framer-motion phase cross-fade
// runs ~150ms during which both steps' primary buttons overlap; a real user needs
// far longer than this to read and act on the next screen, so a second advance
// inside this window is the stray second hit of a double-click, not intent.
const PHASE_ADVANCE_COOLDOWN_MS = 500

// Maps internal reducer phases to the marketer-readable analytics names
// (matched to each screen's eyebrow/title). `paywall`/`done` aren't in the
// onboarding funnel — paywall has its own event family, done is terminal.
const PHASE_TO_ANALYTICS: Partial<Record<Phase, OnboardingPhaseViewedProperties['phase']>> = {
  presence: 'welcome',
  scanning: 'scanning',
  confirm: 'brand_card',
  goals: 'goals',
  focus: 'visuals',
}

interface State {
  phase: Phase
  presence: PresenceData
  scrapeInput?: ScrapeInput
  brandCard?: BrandCardData
  brandKitSaved: boolean
  /**
   * First-business conversational onboarding (desktop + mobile). When true the
   * flow runs scanning -> creatingBusiness -> conversation -> goals -> paywall,
   * replacing the confirm + focus steps. Off ⇒ the classic flow (additional-
   * business, or the business-create fallback). Set once at scan-finished so the
   * reducer's later branches (goals -> paywall vs focus) and back-nav stay
   * consistent across the run.
   */
  conversation: boolean
}

type Action =
  | { type: 'submit-presence'; presence: PresenceData; scrapeInput: ScrapeInput }
  | { type: 'skip-presence'; presence: PresenceData; brandCard: BrandCardData }
  | {
      type: 'scan-finished'
      brandCard: BrandCardData
      presence: PresenceData
      useConversation: boolean
    }
  | { type: 'submit-confirm'; brandCard: BrandCardData }
  | { type: 'business-created'; brandCard: BrandCardData }
  | { type: 'finish-conversation' }
  | { type: 'submit-goals'; brandCard: BrandCardData }
  | { type: 'mark-saved'; brandCard: BrandCardData }
  | { type: 'paywall-done' }
  | { type: 'back' }

const INITIAL: State = {
  phase: 'presence',
  presence: EMPTY_PRESENCE,
  brandKitSaved: false,
  conversation: false,
}

/**
 * stepId for `user.onboardingData.progress.stepId` so we can tell dark-onboarding
 * progress apart from legacy progress. Bump the suffix when the persisted shape
 * changes incompatibly so old in-flight progress doesn't resume into a stale shape.
 */
const PROGRESS_STEP_ID = 'dark-onboarding-v7'

/**
 * Wraps a phase so AnimatePresence has a defined exit signal. Without the
 * explicit `initial` + `animate` pair, framer-motion 11 sometimes never
 * actually runs the exit animation when a motion.div has only an `exit`
 * prop — the outgoing phase then sits in the grid cell indefinitely and
 * its content (including any layoutId-tagged spans) bleeds through the
 * next step. Pinning all three keyframes keeps the lifecycle deterministic.
 *
 * `useIsPresent` + a z-index toggle keeps the entering phase on top of the
 * exiting one during the 150ms fade. OnboardingShell stacks every direct
 * child in the same grid cell, so during a phase swap both phases briefly
 * paint in the same spot — and natural DOM order is "entering on top" only
 * when moving forward through the JSX list. On back-nav (e.g. Confirm →
 * Presence) the exiting phase is later in the DOM and paints over the
 * entering one, which reads as the prev step "stuck" on the new screen
 * until the fade completes.
 */
const PhaseFade: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const isPresent = useIsPresent()
  // framer-motion@11 + React 19: AnimatePresence runs the exit fade but often
  // never unmounts the exited child. The invisible (opacity-0) ghosts pile up
  // in the shell's shared grid cell (the cell tracks the TALLEST ghost, so
  // later phases render off-center and the shell grows a scrollbar) AND their
  // subtrees stay live — effects, store subscriptions, Pusher listeners keep
  // firing from screens the user already left. Until the dependency is
  // upgraded, retire exited phases ourselves once the 150ms fade is over:
  // drop the subtree (children → null) and remove the wrapper from layout.
  // When AnimatePresence does unmount correctly this never has a visible
  // effect. `hidden` also keys on `isPresent` directly so a back-nav
  // re-entry restores the phase in the SAME frame (the retired flag only
  // resets in the effect, which runs after paint).
  const [retired, setRetired] = useState(false)
  useEffect(() => {
    if (isPresent) {
      setRetired(false)
      return
    }
    const t = setTimeout(() => setRetired(true), 250)
    return () => clearTimeout(t)
  }, [isPresent])
  const hidden = retired && !isPresent
  return (
    <motion.div
      className={className}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{
        position: 'relative',
        zIndex: isPresent ? 1 : 0,
        display: hidden ? 'none' : undefined,
      }}
    >
      {hidden ? null : children}
    </motion.div>
  )
}

interface PersistedProgress {
  flow: OnboardingFlow
  phase: Phase
  presence?: PresenceData
  scrapeInput?: ScrapeInput
  brandCard?: BrandCardData
  brandKitSaved: boolean
  conversation?: boolean
}

function rehydrate(persisted: PersistedProgress | null, flowId: OnboardingFlow): State {
  if (!persisted || persisted.flow !== flowId) return INITIAL
  // Scanning + creatingBusiness are transient (their in-flight API call is gone),
  // so resume from Presence with the channels still filled. Done is terminal and
  // is cleared by SegwayStep on completion, so it shouldn't be seen here.
  const phase: Phase =
    persisted.phase === 'scanning' ||
    persisted.phase === 'creatingBusiness' ||
    persisted.phase === 'done'
      ? 'presence'
      : persisted.phase
  return applyDevPhaseOverride({
    phase,
    presence: persisted.presence ?? EMPTY_PRESENCE,
    scrapeInput: persisted.scrapeInput,
    brandCard: persisted.brandCard,
    brandKitSaved: !!persisted.brandKitSaved,
    conversation: !!persisted.conversation,
  })
}

/** DEV-only: `?devPhase=conversation` rewinds to the chat questioner from any saved step. */
function applyDevPhaseOverride(state: State): State {
  if (!import.meta.env.DEV) return state
  const devPhase = new URLSearchParams(window.location.search).get('devPhase')
  if (devPhase !== 'conversation' || !state.brandCard || !state.conversation) return state
  return { ...state, phase: 'conversation' }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'submit-presence':
      return {
        ...state,
        phase: 'scanning',
        presence: action.presence,
        scrapeInput: action.scrapeInput,
        brandCard: undefined,
      }
    case 'skip-presence':
      return {
        ...state,
        phase: 'confirm',
        presence: action.presence,
        scrapeInput: undefined,
        brandCard: action.brandCard,
      }
    case 'scan-finished':
      return {
        ...state,
        // Both flows land on the static confirm card first — the conversational
        // flow shows the same non-chat "here's what we found" result as desktop,
        // then runs the chat AFTER the user confirms (see submit-confirm).
        phase: 'confirm',
        conversation: action.useConversation,
        presence: action.presence,
        brandCard: action.brandCard,
      }
    case 'submit-confirm':
      // Conversational flow creates the business now (so the chat that follows
      // has somewhere to write) then enters the interview; classic flow goes
      // straight to goals and defers the save to the focus step as before.
      return {
        ...state,
        phase: state.conversation ? 'creatingBusiness' : 'goals',
        brandCard: action.brandCard,
      }
    case 'business-created':
      // Business + scanned brand kit are now persisted; enter the conversation.
      return { ...state, phase: 'conversation', brandCard: action.brandCard, brandKitSaved: true }
    case 'finish-conversation':
      return { ...state, phase: 'goals' }
    case 'submit-goals':
      // Conversational flow already saved the kit at creatingBusiness, so goals
      // go straight to the paywall; classic flow still visits the focus step.
      return {
        ...state,
        phase: state.conversation ? 'paywall' : 'focus',
        brandCard: action.brandCard,
      }
    case 'mark-saved':
      return { ...state, brandCard: action.brandCard, brandKitSaved: true, phase: 'paywall' }
    case 'paywall-done':
      return { ...state, phase: 'done' }
    case 'back': {
      const target = prevPhaseFor(state.phase, state.conversation)
      if (!target) return state
      // Rewinding past confirm discards the in-flight brand card so the
      // user can re-submit Presence and re-scan from a clean slate.
      if (target === 'presence') {
        return { ...state, phase: 'presence', brandCard: undefined }
      }
      return { ...state, phase: target }
    }
  }
}

/**
 * Mode controls the two distinct entry paths:
 *  - first-business (default): user has no business yet, flow creates one
 *    using the auth-slice `requiresBusinessCreation` flag and exits through
 *    the SegwayStep wait-for-first-content celebration.
 *  - additional-business: user already has one or more businesses and is
 *    adding another. Flow always creates a new business (regardless of the
 *    auth flag), forces the paywall to show (subscription is 1:1 with
 *    businessId), then calls `onDone` to navigate back into the app.
 */
export type DarkOnboardingMode = 'first-business' | 'additional-business'

export interface DarkOnboardingFlowProps {
  mode?: DarkOnboardingMode
  /** Called once setup completes in additional-business mode. */
  onDone?: () => void
}

/**
 * The unified onboarding flow on the new dark/acid design system.
 *
 * One component handles all four legacy flows. Per-flow differences live in
 * `flow-chrome.ts` (eyebrow copy, secondary CTA, brand-card field visibility).
 *
 * Phase machine:
 *  - classic:        presence -> scanning -> confirm -> goals -> focus -> paywall -> done
 *  - conversational: presence -> scanning -> confirm -> creatingBusiness -> conversation
 *                    -> goals -> paywall -> done  (first-business, when `conversationEnabled`)
 *
 * The brand kit is persisted at confirm (classic defers to focus; the
 * conversational flow saves at creatingBusiness so the chat has a businessId) —
 * not waiting for paywall, so a paywall bounce still keeps the brand profile.
 */
export const DarkOnboardingFlow: React.FC<DarkOnboardingFlowProps> = ({
  mode = 'first-business',
  onDone,
}) => {
  const userStateReady = useAppSelector(selectOnboardingUserStateInitialized)
  if (!userStateReady) {
    return (
      <OnboardingShell>
        <div className="h-12" aria-busy="true" />
      </OnboardingShell>
    )
  }
  return <DarkOnboardingFlowBody mode={mode} onDone={onDone} />
}

const DarkOnboardingFlowBody: React.FC<
  Required<Pick<DarkOnboardingFlowProps, 'mode'>> & Pick<DarkOnboardingFlowProps, 'onDone'>
> = ({ mode, onDone }) => {
  const dispatch = useAppDispatch()
  const { track } = useAnalytics()
  const { addToast } = useToast()
  const { isCliMode } = useCliAuthCallback()
  const flowId = useAppSelector(selectOnboardingFlow)
  const requiresBusinessCreationFromAuth = useAppSelector(selectRequiresBusinessCreation)
  // Additional-business mode always creates a brand new business, regardless
  // of the auth-slice flag (which is false for an existing user adding a 2nd).
  const requiresBusinessCreation =
    mode === 'additional-business' ? true : requiresBusinessCreationFromAuth
  const currentBusinessId = useAppSelector(selectBusinessId)
  const user = useAppSelector(selectCurrentUser)
  const chrome = useMemo(() => getFlowChrome(flowId), [flowId])
  // First-business conversational onboarding, gated by `onboarding_conversation_mobile`
  // (the same flag the mobile rollout used — now shared by desktop too).
  // When on, scanning -> creatingBusiness -> conversation -> goals -> paywall
  // replaces the confirm + focus steps. additional-business never sees it. On
  // business-create failure the flow falls back to the classic confirm form (see
  // creatingBusiness effect below), so the user is never stranded.
  const conversationGate = useFeatureGate(FEATURE_FLAGS.onboardingConversationMobile)
  const conversationEnabled = mode === 'first-business' && conversationGate

  // Lazily seed the reducer from `user.onboardingData.progress`. useReducer's
  // init runs once, so we capture the persisted snapshot from the first render
  // (before the user record refetches). New saves are written via the effect
  // below, not from inside the reducer.
  const [state, action] = useReducer(reducer, INITIAL, init => {
    const saved = user?.onboardingData?.progress
    if (!saved || saved.stepId !== PROGRESS_STEP_ID) return init
    return rehydrate(saved.data as PersistedProgress, flowId)
  })

  const [isSavingConfirm, setIsSavingConfirm] = useState(false)
  // Synchronous gate against double-submits — `isSavingConfirm` is a useState
  // value, so two clicks in the same tick both observe `false` before either
  // setState lands. A ref flips synchronously and locks the second call out.
  // Without this, the focus-step confirm fires two POST /v1/businesses calls
  // and creates two draft businesses.
  const submittingRef = useRef(false)

  // A forward transition briefly stacks the exiting + entering phase in the same
  // grid cell (framer-motion), so the second click of a fast double-click can land
  // on the NEXT phase's primary button and skip a whole step (brand card -> goals
  // -> visuals, skipping goals). Ignore a forward advance that fires within this
  // window of the previous one; a real user can't read and act on two onboarding
  // screens that fast, but the stray second click of a double-click does.
  const lastPhaseAdvanceRef = useRef(0)
  const guardPhaseAdvance = useCallback(() => {
    const now = Date.now()
    if (now - lastPhaseAdvanceRef.current < PHASE_ADVANCE_COOLDOWN_MS) return false
    lastPhaseAdvanceRef.current = now
    return true
  }, [])

  // ── Browser Back = in-wizard back ──────────────────────────────────────
  // Phases are pure component state; with no history entries, the browser's
  // Back left the SPA entirely mid-onboarding (monkey finding: Back at
  // goals/visuals landed on about:blank). Push one entry per forward phase
  // move and translate popstate into the reducer's existing 'back' action;
  // no-escape phases (scanning/creatingBusiness) re-push and stay put.
  const stateRef = useRef(state)
  stateRef.current = state
  const popNavRef = useRef(false)
  const prevPhaseRef = useRef(state.phase)
  useEffect(() => {
    const prev = prevPhaseRef.current
    prevPhaseRef.current = state.phase
    if (state.phase === prev) return
    if (popNavRef.current) {
      // This phase change IS the popstate navigation — the browser already
      // moved its pointer; adding an entry would double it.
      popNavRef.current = false
      return
    }
    if (PHASE_DEPTH[state.phase] > PHASE_DEPTH[prev]) {
      window.history.pushState({ obPhase: state.phase }, '')
    }
  }, [state.phase])
  useEffect(() => {
    const onPop = () => {
      const s = stateRef.current
      if (prevPhaseFor(s.phase, s.conversation)) {
        popNavRef.current = true
        action({ type: 'back' })
      } else if (s.phase !== 'presence' && s.phase !== 'done') {
        window.history.pushState({ obPhase: s.phase }, '')
      }
      // presence/done: nothing to rewind — let the browser leave naturally.
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [action])

  const { status: scanStatus, start: startScrape } = useScanStream()
  // Brand-learning previews fire alongside the scan so we can wait on
  // everything together and land on BrandCardStep with a fully populated
  // card — no in-card skeletons.
  const { state: previewBatch, start: startPreviews } = useBrandLearningPreviewBatch()

  // Ref-deduped so StrictMode's double-mount doesn't double-fire the
  // session start event.
  const startedTrackedRef = useRef(false)
  useEffect(() => {
    if (startedTrackedRef.current) return
    startedTrackedRef.current = true
    track(AnalyticsEvents.Onboarding.ONBOARDING_STARTED, {
      onboarding_flow: flowId,
    } satisfies OnboardingStartedProperties)
  }, [track, flowId])

  // Snapshot of the brand card at the moment the user lands on Phase 3
  // (Scrape's output, before any edits) so we can diff it against the
  // submitted card. Re-set on each entry to confirm so a back-and-rescrape
  // diffs against the second scrape, not the first.
  const brandCardSnapshotRef = useRef<BrandCardData | undefined>(undefined)

  // Per-phase view tracking. Ref-deduped to absorb StrictMode's double-mount
  // and any future identity churn from `track`/`flowId`. Paywall/done are
  // excluded — paywall has its own event family, done is terminal.
  const lastTrackedPhaseRef = useRef<Phase | null>(null)
  useEffect(() => {
    if (lastTrackedPhaseRef.current === state.phase) return
    lastTrackedPhaseRef.current = state.phase
    const analyticsPhase = PHASE_TO_ANALYTICS[state.phase]
    if (!analyticsPhase) return
    track(AnalyticsEvents.Onboarding.ONBOARDING_PHASE_VIEWED, {
      phase: analyticsPhase,
      onboarding_flow: flowId,
    } satisfies OnboardingPhaseViewedProperties)
  }, [state.phase, track, flowId])

  // Persist progress whenever a non-transient phase boundary lands, so that
  // refreshing inside onboarding picks back up where the user left off. We
  // skip the in-flight `scanning` phase (we'd just rewind to presence on
  // reload anyway) and `done` (SegwayStep clears progress as it finalizes).
  const lastPersistedRef = useRef<string>('')
  useEffect(() => {
    if (state.phase === 'scanning' || state.phase === 'creatingBusiness' || state.phase === 'done')
      return
    if (state.phase === 'presence' && !hasPresenceContent(state.presence) && !state.brandCard)
      return

    const payload: PersistedProgress = {
      flow: flowId,
      phase: state.phase,
      presence: state.presence,
      scrapeInput: state.scrapeInput,
      brandCard: state.brandCard,
      brandKitSaved: state.brandKitSaved,
      conversation: state.conversation,
    }
    const fingerprint = JSON.stringify(payload)
    if (fingerprint === lastPersistedRef.current) return
    lastPersistedRef.current = fingerprint

    dispatch(
      updateCurrentUser({
        onboardingData: {
          ...(user?.onboardingData ?? {}),
          flow: flowId,
          progress: {
            stepId: PROGRESS_STEP_ID,
            data: payload as unknown as Record<string, unknown>,
            updatedAt: new Date().toISOString(),
          },
        },
      })
    )
    // We deliberately omit `user` from deps so a fresh user payload from the
    // server (which itself is the result of our save) doesn't cause a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.phase,
    state.presence,
    state.scrapeInput,
    state.brandCard,
    state.brandKitSaved,
    state.conversation,
    flowId,
    dispatch,
  ])

  useEffect(() => {
    if (state.phase !== 'scanning') return
    if (!scanStatus.isFinal) return
    // Wait for brand-learning previews too — landing on BrandCardStep with
    // mining still in-flight defeats the point of pre-fetching.
    if (!previewBatch.isSettled) return
    const { brandCard, presence } = buildScanResult(state, scanStatus, flowId)
    track(AnalyticsEvents.Onboarding.ONBOARDING_SCRAPE_COMPLETED, {
      success: !scanStatus.error,
      had_website: !!state.scrapeInput?.websiteUrl,
      had_instagram: !!state.scrapeInput?.instagramHandle,
      had_facebook: !!state.scrapeInput?.facebookHandle,
      fields_populated: countPopulatedScrapeFields(brandCard),
      had_screenshot: !!scanStatus.screenshot?.url,
      had_sections: (scanStatus.profile?.sections?.length ?? 0) > 0,
    } satisfies OnboardingScrapeCompletedProperties)
    brandCardSnapshotRef.current = brandCard
    action({ type: 'scan-finished', brandCard, presence, useConversation: conversationEnabled })
  }, [state, scanStatus, previewBatch.isSettled, flowId, track, conversationEnabled])

  // Unified presence-submission event — fires on BOTH Continue and Skip so the
  // welcome-phase dashboard can break down input quality regardless of path
  // (valid website? handles auto-discovered vs typed?). `started_scan` is the
  // clean scan-start signal: true only when we actually enter the scanning phase.
  const trackPresenceSubmitted = useCallback(
    (cta: 'continue' | 'skip', meta: PresenceSubmitMeta, startedScan: boolean) => {
      const { sources } = meta
      const handleKeys = [
        'instagramHandle',
        'facebookHandle',
        'linkedinUrl',
        'tiktokHandle',
        'pinterestHandle',
        'googleBusinessUrl',
      ] as const
      const nonEmpty = handleKeys.filter(k => sources[k] !== 'empty')
      track(AnalyticsEvents.Onboarding.ONBOARDING_PRESENCE_SUBMITTED, {
        action: cta,
        started_scan: startedScan,
        has_website: sources.websiteUrl !== 'empty',
        website_valid: meta.websiteValid,
        instagram_source: sources.instagramHandle,
        facebook_source: sources.facebookHandle,
        linkedin_source: sources.linkedinUrl,
        tiktok_source: sources.tiktokHandle,
        pinterest_source: sources.pinterestHandle,
        google_business_provided: sources.googleBusinessUrl !== 'empty',
        handle_count: nonEmpty.length,
        autofilled_count: nonEmpty.filter(k => sources[k] === 'autofilled').length,
        manual_count: nonEmpty.filter(k => sources[k] === 'manual').length,
        onboarding_flow: flowId,
      } satisfies OnboardingPresenceSubmittedProperties)
    },
    [track, flowId]
  )

  const handlePresenceSubmit = useCallback(
    (presence: PresenceData, scrapeInput: ScrapeInput, meta: PresenceSubmitMeta) => {
      track(AnalyticsEvents.Onboarding.ONBOARDING_STEP_WEBSITE_URL_SUBMITTED, {
        website_url: scrapeInput.websiteUrl ?? '',
        has_instagram: !!scrapeInput.instagramHandle,
        has_facebook: !!scrapeInput.facebookHandle,
        input_count:
          (scrapeInput.websiteUrl ? 1 : 0) +
          (scrapeInput.instagramHandle ? 1 : 0) +
          (scrapeInput.facebookHandle ? 1 : 0),
      } satisfies OnboardingStepWebsiteUrlSubmittedProperties)
      trackPresenceSubmitted('continue', meta, meta.scrapeable)

      if (!meta.scrapeable) {
        // Nothing the scan can use (only an invalid-format website was entered)
        // — skip the scanning phase entirely and land on the brand card, the
        // same way the explicit Skip link does.
        const brandCard = emptyBrandCard(presence, flowId)
        brandCardSnapshotRef.current = brandCard
        action({ type: 'skip-presence', presence, brandCard })
        return
      }

      action({ type: 'submit-presence', presence, scrapeInput })
      startScrape(scrapeInput)
      // Mine the user's IG / website for brand-learning signals in parallel.
      // ScanningStep narrates progress for both; the parent gates the phase
      // transition on both finishing.
      startPreviews({
        instagramHandle: scrapeInput.instagramHandle,
        websiteUrl: scrapeInput.websiteUrl,
      })
    },
    [startScrape, startPreviews, track, trackPresenceSubmitted, flowId]
  )

  const handlePresenceSkip = useCallback(
    (presence: PresenceData, meta: PresenceSubmitMeta) => {
      const brandCard = emptyBrandCard(presence, flowId)
      track(AnalyticsEvents.Onboarding.ONBOARDING_STEP_WEBSITE_URL_SKIPPED, {
        method: hasPresenceContent(presence) ? 'continue_empty' : 'skip_button',
      } satisfies OnboardingStepWebsiteUrlSkippedProperties)
      trackPresenceSubmitted('skip', meta, false)
      // Skip path produces an empty brand card; subsequent edits all count
      // as user-edited in the diff.
      brandCardSnapshotRef.current = brandCard
      action({ type: 'skip-presence', presence, brandCard })
    },
    [flowId, track, trackPresenceSubmitted]
  )

  const persistBrandKit = useCallback(
    async (card: BrandCardData, presence: PresenceData) => {
      const channels: Record<string, unknown> = {}
      if (presence.instagramHandle) channels.instagram = { handle: presence.instagramHandle }
      if (presence.facebookHandle) channels.facebook = { handle: presence.facebookHandle }
      if (presence.linkedinUrl) channels.linkedin = { handle: presence.linkedinUrl }
      if (presence.tiktokHandle) channels.tiktok = { handle: presence.tiktokHandle }
      if (presence.pinterestHandle) channels.pinterest = { handle: presence.pinterestHandle }
      if (presence.googleBusinessUrl) channels.googleBusiness = { url: presence.googleBusinessUrl }

      const brandKit: Record<string, unknown> = {
        businessName: card.businessName,
        industry: card.industry,
        description: card.description,
        targetAudience: card.targetAudience,
        toneOfVoice: card.tones.length ? card.tones : ['friendly'],
        goals: card.goals,
        channels,
        contentFocus: card.contentFocus,
      }
      if (card.colors) brandKit.colors = card.colors
      if (presence.websiteUrl) brandKit.websiteUrl = presence.websiteUrl
      if (card.keywords?.length) brandKit.keywords = card.keywords
      const completedImages = card.images
        .filter(img => img.attachmentId)
        .map(img => ({ attachmentId: img.attachmentId, tags: img.tags }))
      if (completedImages.length) brandKit.images = completedImages

      let activeBusinessId: string | undefined
      if (requiresBusinessCreation) {
        // Wrap the POST /v1/businesses dispatch with create-lifecycle events
        // so the actual server call is observable (audit on 2026-05-31 found
        // the failure path had zero client-side trace — only ONBOARDING_*
        // milestone events fired).
        const flow: BusinessCreateRequestedProperties['flow'] =
          mode === 'additional-business' ? 'additional_business' : 'first_business'
        track(AnalyticsEvents.Onboarding.BUSINESS_CREATE_REQUESTED, {
          flow,
        } satisfies BusinessCreateRequestedProperties)
        try {
          const business = await dispatch(
            createBusinessAction({ name: card.businessName })
          ).unwrap()
          track(AnalyticsEvents.Onboarding.BUSINESS_CREATE_SUCCEEDED, {
            flow,
            business_id: business.id,
          } satisfies BusinessCreateSucceededProperties)
          await dispatch(selectBusinessSetupAction({ businessId: business.id })).unwrap()
          activeBusinessId = business.id
        } catch (err) {
          const errAny = err as { statusCode?: number; message?: string; error?: string }
          track(AnalyticsEvents.Onboarding.BUSINESS_CREATE_FAILED, {
            flow,
            error_message: errAny?.message ?? errAny?.error ?? String(err),
            ...(typeof errAny?.statusCode === 'number' ? { status_code: errAny.statusCode } : {}),
          } satisfies BusinessCreateFailedProperties)
          throw err
        }
      } else {
        await dispatch(updateBusinessAction({ name: card.businessName })).unwrap()
        activeBusinessId = currentBusinessId ?? undefined
      }

      // Flush brand-learning chip edits the user made on BrandCardStep before
      // the business existed (or in additional-business mode, against the
      // previously-active business). Each call is independent — failures here
      // must NOT block the rest of persistBrandKit because the brand kit
      // save is more important than chip metadata. Errors are surfaced to
      // the console for debugging but swallowed otherwise.
      const pendingOverrides = card.pendingBrandLearningOverrides ?? []
      if (pendingOverrides.length > 0 && activeBusinessId) {
        await Promise.all(
          pendingOverrides.map(async op => {
            try {
              if (op.value === null) {
                await clearBrandLearningOverride(activeBusinessId!, op.source, op.fieldPath)
              } else {
                await setBrandLearningOverride(activeBusinessId!, op.source, op.fieldPath, op.value)
              }
            } catch (err) {
              console.warn(
                '[DarkOnboarding] flush brand-learning override failed:',
                op.fieldPath,
                err
              )
            }
          })
        )
      }

      const isExternalUrl = (v: string) => v.startsWith('http://') || v.startsWith('https://')
      if (card.logoUrl && isExternalUrl(card.logoUrl)) {
        try {
          const r = await downloadLogo(card.logoUrl)
          brandKit.logoId = r.attachmentId
        } catch (err) {
          console.warn('[DarkOnboarding] Logo download failed:', err)
        }
      }

      const saved = await patchBrandKit(brandKit as Parameters<typeof patchBrandKit>[0])

      // Flush brand-learning preview blobs the user got during the pending-business
      // window. Each call is independent — failures fall through to the server-side
      // kickoff (runInitialMining) which will re-mine the affected source. T7's
      // skip-if-seeded guard makes sure we don't double-mine on the seeded sources.
      const pendingPreviews = card.pendingBrandLearningPreviews ?? []
      if (pendingPreviews.length > 0 && activeBusinessId) {
        await Promise.all(
          pendingPreviews.map(async preview => {
            try {
              await seedBrandLearningFromPreview({
                businessId: activeBusinessId!,
                source: preview.source,
                aggregates: preview.response.aggregates,
                rawPerPostData: preview.response.rawPerPostData,
                previewedWith: preview.previewedWith,
              })
            } catch (err) {
              console.warn(
                '[DarkOnboarding] seedBrandLearningFromPreview failed:',
                preview.source,
                err
              )
            }
          })
        )
      }

      useBrandKitStore.getState().setBrandKitFromApi(saved)
    },
    [dispatch, requiresBusinessCreation, currentBusinessId]
  )

  // Conversational flow: the moment we land on the transient creatingBusiness
  // step, create the business and persist the scanned brand kit so the chat that
  // follows has a real businessId to write voice / look / guidelines into. On
  // success enter the conversation; on failure fall back to the classic confirm
  // form so the user is never stranded. Ref-guarded against StrictMode/re-render
  // double-fire (a double-create is exactly what submittingRef guards elsewhere).
  const creatingBusinessRef = useRef(false)
  useEffect(() => {
    if (state.phase !== 'creatingBusiness' || !state.brandCard) return
    if (creatingBusinessRef.current) return
    creatingBusinessRef.current = true
    const card = state.brandCard
    void (async () => {
      try {
        await persistBrandKit(card, state.presence)
        track(AnalyticsEvents.Onboarding.ONBOARDING_COMPLETED, {
          source: isCliMode ? 'cli' : 'web',
          onboarding_flow: flowId,
        } satisfies OnboardingCompletedProperties)
        action({ type: 'business-created', brandCard: card })
      } catch (err) {
        console.error('[DarkOnboarding] Conversational business create failed:', err)
        addToast('error', 'Setup hit a snag. Continuing the classic way.', 5000)
        // Drop back into the classic confirm form (conversation = false).
        action({
          type: 'scan-finished',
          brandCard: card,
          presence: state.presence,
          useConversation: false,
        })
      } finally {
        creatingBusinessRef.current = false
      }
    })()
  }, [
    state.phase,
    state.brandCard,
    state.presence,
    persistBrandKit,
    track,
    isCliMode,
    flowId,
    addToast,
  ])

  const handleConfirmSubmit = useCallback(
    (card: BrandCardData) => {
      if (!guardPhaseAdvance()) return
      track(AnalyticsEvents.Onboarding.ONBOARDING_BRAND_CARD_SUBMITTED, {
        fields_edited: diffBrandCardFields(brandCardSnapshotRef.current, card),
        tones_count: card.tones.length,
      } satisfies OnboardingBrandCardSubmittedProperties)
      action({ type: 'submit-confirm', brandCard: card })
    },
    [track, guardPhaseAdvance]
  )

  const handleGoalsSubmit = useCallback(
    (goals: BrandCardData['goals']) => {
      if (!guardPhaseAdvance()) return
      if (!state.brandCard) return
      if (state.conversation) {
        // The kit was already saved at creatingBusiness; persist just the goals
        // the user picked now so the paywall + first posts reflect them. Classic
        // flow defers all saves to the focus step, so it skips this.
        void patchBrandKit({ goals }).catch(err =>
          console.error('[DarkOnboarding] goals save failed:', err)
        )
      }
      action({ type: 'submit-goals', brandCard: { ...state.brandCard, goals } })
    },
    [state.brandCard, state.conversation, guardPhaseAdvance]
  )

  const handleFocusSubmit = useCallback(
    async (focusData: {
      contentFocus: BrandCardData['contentFocus']
      images: BrandCardData['images']
    }) => {
      if (!guardPhaseAdvance()) return
      if (!state.brandCard) return
      if (submittingRef.current) return
      submittingRef.current = true
      // Only signal of upload behaviour in the dark flow: photos are optional and
      // `onboarding_completed` carries no image data. Fire on submit intent (like
      // brand_card) so abandonment during the save still records what the user did.
      // FocusStep shows 'other' when the scraper left contentFocus null, so compare
      // against that same default to know if the user actually overrode the classifier.
      track(AnalyticsEvents.Onboarding.ONBOARDING_VISUALS_SUBMITTED, {
        content_focus: focusData.contentFocus ?? 'other',
        image_count: focusData.images.length,
        has_images: focusData.images.length > 0,
        content_focus_changed: focusData.contentFocus !== (state.brandCard.contentFocus ?? 'other'),
      } satisfies OnboardingVisualsSubmittedProperties)
      const card: BrandCardData = {
        ...state.brandCard,
        contentFocus: focusData.contentFocus,
        images: focusData.images,
      }
      setIsSavingConfirm(true)
      try {
        await persistBrandKit(card, state.presence)
        action({ type: 'mark-saved', brandCard: card })
        track(AnalyticsEvents.Onboarding.ONBOARDING_COMPLETED, {
          source: isCliMode ? 'cli' : 'web',
          onboarding_flow: flowId,
        } satisfies OnboardingCompletedProperties)
      } catch (err) {
        console.error('[DarkOnboarding] Brand kit save failed:', err)
        addToast('error', 'Save failed. Try again in a moment.', 5000)
      } finally {
        setIsSavingConfirm(false)
        submittingRef.current = false
      }
    },
    [
      persistBrandKit,
      addToast,
      track,
      flowId,
      isCliMode,
      state.presence,
      state.brandCard,
      guardPhaseAdvance,
    ]
  )

  // Memoized so SegwayStep's effect doesn't churn on every parent re-render
  // (an inline arrow here re-armed its timers each render for the whole hold).
  const saveSegwayBrandKit = useCallback(async () => {
    if (state.brandCard) await persistBrandKit(state.brandCard, state.presence)
  }, [state.brandCard, state.presence, persistBrandKit])

  const handlePaywallNext = useCallback(async () => {
    // Flip `setupCompletedAt` on the (just-purchased) business so it leaves
    // the draft state and becomes visible in the switcher. Idempotent on the
    // server, so safe for first-business mode too where the business has
    // technically been "complete" since the LP-scan migration backfilled
    // every existing row. Only meaningful for additional-business, but
    // calling it unconditionally keeps the contract simple.
    // `currentBusinessId` is a non-empty string sentinel ('-1') when the user
    // hasn't selected a business yet — truthy on its own but illegal to send
    // to the server. Every other dispatch site in this file already filters
    // for !== PENDING_BUSINESS_ID; keep this one consistent so we don't fire
    // markSetupComplete with bid='-1' and surface a NotFoundException to
    // error tracking via the console bridge.
    if (currentBusinessId && currentBusinessId !== PENDING_BUSINESS_ID) {
      try {
        await dispatch(markBusinessSetupCompleteAction({ businessId: currentBusinessId })).unwrap()
      } catch (err) {
        // Surface the error but do not block the user — silent-refresh on the
        // next page load will retry against a server that already accepts
        // re-completion as a no-op.
        console.warn('[DarkOnboarding] markSetupComplete failed:', err)
      }
    }
    action({ type: 'paywall-done' })
  }, [dispatch, currentBusinessId])
  const handleBack = useCallback(() => action({ type: 'back' }), [])
  // Scanning is intentionally a no-escape moment — Back during a scrape would
  // either cancel the in-flight API or discard the URL the user typed, both of
  // which break the trust this screen is asking for.
  // The conversational flow now has the full back chain (paywall → goals →
  // conversation → confirm → presence), so the chevron renders on every step
  // except the no-escape scanning + creatingBusiness moments.
  const canGoBack =
    state.phase === 'confirm' ||
    state.phase === 'focus' ||
    state.phase === 'goals' ||
    state.phase === 'paywall' ||
    (state.phase === 'conversation' && state.conversation)

  return (
    <OnboardingShell onBack={canGoBack ? handleBack : undefined}>
      {/* AnimatePresence keeps the outgoing scanning step mounted during the
          enter animation of the brand card so shared `layoutId` elements
          (favicon, business name, color swatches, active tone chips) can fly
          from their reveal positions on Scanning into their final slots on
          the Brand Card.

          Each phase is wrapped in a motion.div with a fast exit-only fade.
          Without `exit`, AnimatePresence holds the outgoing child for one
          render tick before unmounting — long enough that going back from
          Confirm → Presence (and the analogous goals/focus/paywall back
          paths) sometimes flashes both phases stacked in the same grid cell
          (OnboardingShell's `[&>*]:col-start-1` overlap). The exit prop
          fires only on exit (no `animate` cascade during enter), so the
          incoming layoutId children stay at full opacity through their morph.

          ScanningStep is ALSO wrapped in PhaseFade so AnimatePresence
          tracks its lifecycle and unmounts it cleanly. Without the wrapper,
          the source-side layoutId children (the serif-italic name, the
          scan-row swatches/chips) routinely orphan at the original
          ScanningStep position and stay painted on top of BrandCardStep
          (visible as a ghost "Pierre Hermé" above the card). The
          BrandCardStep target drops its matching layoutIds shortly after
          mount (see `morphSettled` there), so by the time scanning's
          150ms fade completes, there's nothing left to morph — the
          source just dissolves. */}
      <AnimatePresence>
        {state.phase === 'presence' && (
          <PhaseFade key="presence">
            <PresenceStep
              chrome={chrome}
              initial={state.presence}
              onSubmit={handlePresenceSubmit}
              onSkip={handlePresenceSkip}
            />
          </PhaseFade>
        )}
        {state.phase === 'scanning' && (
          <PhaseFade key="scanning">
            <ScanningStep
              status={scanStatus}
              target={describeScrapeInput(state.scrapeInput)}
              websiteUrl={state.scrapeInput?.websiteUrl}
              brandLearningProgress={{
                instagram: previewBatch.instagram?.phase ?? null,
                website: previewBatch.website?.phase ?? null,
              }}
            />
          </PhaseFade>
        )}
        {state.phase === 'confirm' && state.brandCard && (
          <PhaseFade key="confirm">
            <BrandCardStep
              initial={state.brandCard}
              scanError={scanStatus.error}
              scanSource={pickScrapeKind(state.scrapeInput ?? {})}
              isSubmitting={isSavingConfirm}
              nextIsChat={state.conversation}
              onSubmit={handleConfirmSubmit}
              prefetchedBrandLearning={
                previewBatch.attemptedCount > 0
                  ? {
                      summary: getBatchSummary(previewBatch),
                      pendingPreviews: getBatchPendingPreviews(previewBatch),
                    }
                  : undefined
              }
            />
          </PhaseFade>
        )}
        {/* creatingBusiness + conversation share ONE mounted ConversationStep so
            it stays put while the business is created in the background (the
            interview shows a "thinking" state); the first question appears the
            moment the businessId is real and the seed fires. */}
        {(state.phase === 'creatingBusiness' || state.phase === 'conversation') &&
          state.brandCard && (
            <PhaseFade key="conversation" className="self-start">
              <ConversationStep
                businessId={currentBusinessId ?? ''}
                brandCard={state.brandCard}
                onDone={() => action({ type: 'finish-conversation' })}
              />
            </PhaseFade>
          )}
        {state.phase === 'goals' && state.brandCard && (
          <PhaseFade key="goals">
            <GoalsStep initial={state.brandCard.goals} onSubmit={handleGoalsSubmit} />
          </PhaseFade>
        )}
        {state.phase === 'focus' && state.brandCard && (
          <PhaseFade key="focus">
            <FocusStep
              initial={state.brandCard}
              isSubmitting={isSavingConfirm}
              onSubmit={handleFocusSubmit}
            />
          </PhaseFade>
        )}
        {state.phase === 'paywall' && (
          <PhaseFade key="paywall">
            <DarkPaywallStep
              flowId={flowId}
              onNext={handlePaywallNext}
              forceShow={mode === 'additional-business'}
              personalization={
                state.brandCard
                  ? buildTrialPersonalization(state.brandCard, state.presence)
                  : undefined
              }
            />
          </PhaseFade>
        )}
        {state.phase === 'done' && mode === 'first-business' && (
          <PhaseFade key="done">
            <SegwayStep
              brandKitAlreadySaved={state.brandKitSaved}
              saveBrandKit={saveSegwayBrandKit}
            />
          </PhaseFade>
        )}
        {state.phase === 'done' && mode === 'additional-business' && (
          <PhaseFade key="done-additional">
            <AdditionalBusinessDone onDone={onDone} />
          </PhaseFade>
        )}
      </AnimatePresence>
    </OnboardingShell>
  )
}

/**
 * Terminal step for additional-business mode. The new business is now visible
 * (setupCompletedAt was flipped in handlePaywallNext) and the JWT already
 * carries the new businessId (persistBrandKit called selectBusinessSetupAction).
 * Clear the persisted progress slot so a future additional-business flow
 * doesn't resume into stale state, then fire `onDone` so the page-level
 * container can navigate. No SegwayStep here: that screen is tied to
 * first-content priming and onboardingCompleted, both of which are already
 * done for this user.
 */
const AdditionalBusinessDone: React.FC<{ onDone?: () => void }> = ({ onDone }) => {
  const dispatch = useAppDispatch()
  const { track } = useAnalytics()
  const user = useAppSelector(selectCurrentUser)
  const currentBusinessId = useAppSelector(selectBusinessId)
  const firedRef = useRef(false)
  // firedRef gates the once-only behaviour; including the real deps keeps
  // the linter happy without changing semantics. Re-renders flip nothing.
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    if (currentBusinessId) {
      track(AnalyticsEvents.Sidebar.ADDITIONAL_BUSINESS_COMPLETED, {
        business_id: currentBusinessId,
      } satisfies AdditionalBusinessCompletedProperties)
    }
    void (async () => {
      try {
        await dispatch(
          updateCurrentUser({
            onboardingData: {
              ...(user?.onboardingData ?? {}),
              progress: null,
            },
          })
        ).unwrap()
      } catch (err) {
        console.warn('[AdditionalBusinessDone] Failed to clear progress:', err)
      } finally {
        onDone?.()
      }
    })()
  }, [currentBusinessId, dispatch, onDone, track, user?.onboardingData])
  return (
    <div className="mx-auto flex max-w-[360px] flex-col items-center gap-3 text-center">
      <p className="text-[15px] text-paper">Setting up your new business…</p>
    </div>
  )
}

function buildScanResult(
  state: State,
  scan: ScanStatus,
  _flowId: OnboardingFlow
): { brandCard: BrandCardData; presence: PresenceData } {
  const r = scan.profile
  const input = state.scrapeInput ?? {}
  const previousPresence = state.presence
  const fallbackName = presenceDerivedName(previousPresence)
  const brandCard: BrandCardData = {
    businessName: r?.businessName?.trim() || fallbackName || '',
    industry: r?.industry ?? '',
    description: r?.description ?? '',
    targetAudience: r?.targetAudience ?? '',
    tones: normalizeTones(r?.suggestedTones),
    goals: normalizeGoals(r?.suggestedGoals),
    colors: r?.colors,
    logoUrl: r?.logoUrl ?? undefined,
    logoBackdrop: r?.logoBackdrop,
    keywords: r?.keywords,
    contentFocus: r
      ? classifyContentFocus({
          industry: r.industry,
          description: r.description,
          keywords: r.keywords,
        })
      : null,
    images: [],
  }
  // Preserve everything the user typed on Presence; fold in scrape discoveries
  // (instagram/facebook/linkedin) only where the user left a slot empty.
  const presence: PresenceData = {
    ...previousPresence,
    websiteUrl: previousPresence.websiteUrl || input.websiteUrl || r?.websiteUrl || '',
    instagramHandle: previousPresence.instagramHandle || r?.socialHandles?.instagram || '',
    facebookHandle: previousPresence.facebookHandle || r?.socialHandles?.facebook || '',
    linkedinUrl: previousPresence.linkedinUrl || r?.socialHandles?.linkedin || '',
    tiktokHandle: previousPresence.tiktokHandle || r?.socialHandles?.tiktok || '',
    pinterestHandle: previousPresence.pinterestHandle || r?.socialHandles?.pinterest || '',
  }
  return { brandCard, presence }
}

/** Empty brand card seeded from whatever the user typed on Presence. */
function emptyBrandCard(presence: PresenceData, _flowId: OnboardingFlow): BrandCardData {
  return {
    businessName: presenceDerivedName(presence),
    industry: '',
    description: '',
    targetAudience: '',
    tones: [],
    goals: [],
    contentFocus: null,
    images: [],
  }
}

/**
 * Derive a starter business name from whatever the user gave us on Presence.
 * Lets the brand card open with *something* even when the scrape returns
 * nothing or the user took the skip path.
 */
function presenceDerivedName(presence: PresenceData): string {
  if (presence.websiteUrl) {
    const root = parseHostname(presence.websiteUrl)?.split('.')[0]
    if (root) return capitalize(root)
  }
  if (presence.instagramHandle) return capitalize(presence.instagramHandle)
  if (presence.facebookHandle) return capitalize(presence.facebookHandle)
  return ''
}

function hasPresenceContent(presence: PresenceData): boolean {
  return (
    !!presence.websiteUrl ||
    !!presence.instagramHandle ||
    !!presence.facebookHandle ||
    !!presence.linkedinUrl ||
    !!presence.tiktokHandle ||
    !!presence.pinterestHandle ||
    !!presence.googleBusinessUrl
  )
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Channels we'd talk about on the paywall — websiteUrl is excluded since
    "auto-published to your website" is nonsensical. */
const PAYWALL_CHANNEL_KEYS: ReadonlyArray<keyof PresenceData> = [
  'instagramHandle',
  'facebookHandle',
  'linkedinUrl',
  'googleBusinessUrl',
]

function buildTrialPersonalization(
  card: BrandCardData,
  presence: PresenceData
): TrialPersonalization {
  const businessName = card.businessName?.trim() || 'your brand'
  const channelLabels = PAYWALL_CHANNEL_KEYS.filter(k => presence[k]).map(
    k => PRESENCE_FIELD_LABELS[k]
  )
  const goalLabels = card.goals
    .map(g => GENERAL_GOAL_OPTIONS.find(o => o.id === g)?.label)
    .filter((l): l is string => !!l)
    .slice(0, 2)
    .map(l => l.toLowerCase())
  return { businessName, channelLabels, goalLabels }
}

function describeScrapeInput(input: ScrapeInput | undefined): string | undefined {
  if (!input) return undefined
  if (input.websiteUrl) {
    return parseWebsiteHost(input.websiteUrl) ?? input.websiteUrl
  }
  if (input.instagramHandle) return `@${input.instagramHandle}`
  if (input.facebookHandle) return `fb.com/${input.facebookHandle}`
  return undefined
}

// Out of the 6 brand-card fields the scraper aims to populate, count how
// many actually came back filled. Reads from the post-mapped BrandCardData
// (so businessName may include presence-derived fallbacks) — not directly
// comparable with legacy `ANALYZING_WEBSITE_COMPLETED.fields_populated`.
function countPopulatedScrapeFields(card: BrandCardData): number {
  let n = 0
  if (card.businessName.trim()) n++
  if (card.industry.trim()) n++
  if (card.description.trim()) n++
  if (card.targetAudience.trim()) n++
  if (card.logoUrl) n++
  if (card.colors) n++
  return n
}

// Diff the user's submitted brand card against the scraper's output to
// learn which fields they actually changed. Empty snapshot (skip-presence
// path) means every populated field counts as edited.
function diffBrandCardFields(
  before: BrandCardData | undefined,
  after: BrandCardData
): OnboardingBrandCardSubmittedProperties['fields_edited'] {
  const edited: OnboardingBrandCardSubmittedProperties['fields_edited'] = []
  const stringChanged = (a: string | undefined | null, b: string | undefined | null) =>
    (a ?? '').trim() !== (b ?? '').trim()
  if (stringChanged(before?.businessName, after.businessName)) edited.push('businessName')
  if (stringChanged(before?.industry, after.industry)) edited.push('industry')
  if (stringChanged(before?.description, after.description)) edited.push('description')
  if (stringChanged(before?.targetAudience, after.targetAudience)) edited.push('targetAudience')
  if (JSON.stringify(before?.tones ?? []) !== JSON.stringify(after.tones)) edited.push('tones')
  if (stringChanged(before?.logoUrl, after.logoUrl)) edited.push('logoUrl')
  if (JSON.stringify(before?.colors ?? null) !== JSON.stringify(after.colors ?? null)) {
    edited.push('colors')
  }
  return edited
}
